import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { useGetMenuByIdQuery } from "@/redux/services/menu/menu.api";
import toast from "react-hot-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/Label";
import { formatSideType } from "@/lib/utils";
import Select from "react-select";
import {
  AllowedSideType,
  MenuCombo,
  MenuComboComponent,
  MenuComboComponentFormData,
} from "@/redux/services/menu/menu_combo.type";
import {
  useCreateMenuComboComponentMutation,
  useUpdateMenuComboComponentMutation,
} from "@/redux/services/menu/menu_combo.api";
import { logger } from "@/services/Logger";
import { toastApiError } from "@/lib/apiErrors";

interface MenuComboComponentModalProps {
  isOpen: boolean;
  onClose: () => void;
  menuCombo: MenuCombo;
  component?: MenuComboComponent;
  onSuccess?: () => void;
}

const MenuComboComponentModal: React.FC<MenuComboComponentModalProps> = ({
  isOpen,
  onClose,
  component,
  menuCombo,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [sortOrder, setSortOrder] = useState(1);
  const [isRequired, setIsRequired] = useState(true);
  const [isSubstitutable, setIsSubstitutable] = useState(false);
  const [maxChoiceCount, setMaxChoiceCount] = useState(1);
  type SelectOption = { value: string; label: string };

  const [selectedMenuSection, setSelectedMenuSection] = useState<SelectOption | null>(null);
  const [selectedIncludedItems, setSelectedIncludedItems] = useState<SelectOption[]>([]);
  const [selectedExcludedItems, setSelectedExcludedItems] = useState<SelectOption[]>([]);
  const [selectedAllowedSideTypes, setSelectedAllowedSideTypes] = useState<SelectOption[]>([]);

  const { data: menu } = useGetMenuByIdQuery(menuCombo.menu.id);

  const [createComponent, { isLoading: isCreating }] =
    useCreateMenuComboComponentMutation();
  const [updateComponent, { isLoading: isUpdating }] =
    useUpdateMenuComboComponentMutation();

  const isEditing = !!component;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (component) {
      setName(component.name);
      setDescription(component.description ?? "");
      setQuantity(component.quantity);
      setSortOrder(component.sortOrder);
      setIsRequired(component.isRequired);
      setIsSubstitutable(component.isSubstitutable);
      setMaxChoiceCount(component.maxChoiceCount);

      if (component.menuSection) {
        setSelectedMenuSection({
          value: component.menuSection.id,
          label: component.menuSection.title,
        });
      } else {
        setSelectedMenuSection(null);
      }

      if (component.includedMenuItems.length > 0) {
        setSelectedIncludedItems(
          component.includedMenuItems.map((item) => ({
            value: item.id,
            label: item.title,
          }))
        );
      }

      if (component.excludedMenuItems.length > 0) {
        setSelectedExcludedItems(
          component.excludedMenuItems.map((item) => ({
            value: item.id,
            label: item.title,
          }))
        );
      }

      if (component.allowedSideTypes.length > 0) {
        setSelectedAllowedSideTypes(
          component.allowedSideTypes.map((type) => ({
            value: type,
            label: formatSideType(type),
          }))
        );
      }
    } else {
      setName("");
      setDescription("");
      setQuantity(1);
      setSortOrder(1);
      setIsRequired(true);
      setIsSubstitutable(false);
      setMaxChoiceCount(1);
      setSelectedMenuSection(null);
      setSelectedIncludedItems([]);
      setSelectedExcludedItems([]);
      setSelectedAllowedSideTypes([]);
    }
  }, [component, isOpen]);

  const sectionOptions =
    menu?.menuSections.map((section) => ({
      value: section.id,
      label: section.title,
    })) || [];

  const getIncludedMenuItems = () => {
    if (!menu) return [];

    if (!selectedMenuSection) {
      return menu.menuSections.flatMap((section) =>
        section.menuItems.map((item) => ({
          value: item.id,
          label: `${section.title} - ${item.title}`,
        }))
      );
    }

    return menu.menuSections
      .filter((section) => section.id !== selectedMenuSection.value)
      .flatMap((section) =>
        section.menuItems.map((item) => ({
          value: item.id,
          label: `${section.title} - ${item.title}`,
        }))
      );
  };

  const getExcludedMenuItems = () => {
    if (!menu) return [];

    if (selectedMenuSection) {
      return menu.menuSections
        .filter((section) => section.id === selectedMenuSection.value)
        .flatMap((section) =>
          section.menuItems.map((item) => ({
            value: item.id,
            label: item.title,
          }))
        );
    }

    return menu.menuSections.flatMap((section) =>
      section.menuItems.map((item) => ({
        value: item.id,
        label: `${section.title} - ${item.title}`,
      }))
    );
  };

  const sideTypeOptions = [
    { value: AllowedSideType.FRIES, label: "Frites" },
    { value: AllowedSideType.SALADS, label: "Salades" },
  ];

  const validateForm = (): boolean => {
    if (!name.trim()) {
      toast.error("Le nom du composant est requis");
      return false;
    }

    if (quantity <= 0) {
      toast.error("La quantité doit être supérieure à zéro");
      return false;
    }

    if (maxChoiceCount <= 0) {
      toast.error("Le nombre maximum de choix doit être supérieur à zéro");
      return false;
    }

    if (
      !selectedMenuSection &&
      selectedIncludedItems.length === 0 &&
      selectedAllowedSideTypes.length === 0
    ) {
      toast.error(
        "Vous devez spécifier soit une section, des éléments inclus, ou des types d'accompagnement autorisés"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const componentData: MenuComboComponentFormData = {
        name,
        description,
        quantity,
        sortOrder,
        isRequired,
        isSubstitutable,
        maxChoiceCount,
        menuCombo: `/api/menu_combos/${menuCombo.id}`,
        allowedSideTypes: selectedAllowedSideTypes.map((item) => item.value),
      };

      if (selectedMenuSection) {
        componentData.menuSection = `/api/menu_sections/${selectedMenuSection.value}`;
      } else {
        componentData.menuSection = null;
      }

      if (selectedIncludedItems.length > 0) {
        componentData.includedMenuItems = selectedIncludedItems.map(
          (item) => `/api/menu_items/${item.value}`
        );
      } else {
        componentData.includedMenuItems = [];
      }

      if (selectedExcludedItems.length > 0) {
        componentData.excludedMenuItems = selectedExcludedItems.map(
          (item) => `/api/menu_items/${item.value}`
        );
      } else {
        componentData.excludedMenuItems = [];
      }

      if (isEditing && component) {
        await updateComponent({
          id: component.id,
          data: componentData,
        }).unwrap();

        toast.success("Composant mis à jour avec succès");
      } else {
        await createComponent(componentData).unwrap();
        toast.success("Composant créé avec succès");
      }

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error: any) {
      logger.error(
        "MenuComboComponentModal",
        "Error saving menu combo component:",
        error
      );

      toastApiError(error, "Erreur lors de l'enregistrement du composant");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier le composant" : "Ajouter un composant"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Nom"
                  type="text"
                  id="name"
                  placeholder="Nom du composant"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <Input
                  label="Quantité"
                  type="number"
                  id="quantity"
                  placeholder="Quantité"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>

            <div>
              <TextArea
                label="Description"
                placeholder="Description du composant"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Position"
                  type="number"
                  id="sortOrder"
                  placeholder="Position"
                  min={1}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value) || 1)}
                />
              </div>

              <div>
                <Input
                  label="Nombre maximum de choix"
                  type="number"
                  id="maxChoiceCount"
                  placeholder="Nombre maximum de choix"
                  min={1}
                  value={maxChoiceCount}
                  onChange={(e) =>
                    setMaxChoiceCount(parseInt(e.target.value) || 1)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isRequired"
                  checked={isRequired}
                  onCheckedChange={(checked) =>
                    setIsRequired(checked as boolean)
                  }
                />
                <Label
                  htmlFor="isRequired"
                  label="Requis"
                  className="cursor-pointer"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isSubstitutable"
                  checked={isSubstitutable}
                  onCheckedChange={(checked) =>
                    setIsSubstitutable(checked as boolean)
                  }
                />
                <Label
                  htmlFor="isSubstitutable"
                  label="Substituable"
                  className="cursor-pointer"
                />
              </div>
            </div>

            <div>
              <Label label="Section" htmlFor="menuSection" />
              <Select
                id="menuSection"
                options={sectionOptions}
                value={selectedMenuSection}
                onChange={(option) => setSelectedMenuSection(option)}
                isClearable
                placeholder="Aucune section spécifique"
                className="basic-select"
                classNamePrefix="select"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si sélectionné, les clients pourront choisir parmi les éléments
                de cette section
              </p>
            </div>

            <div>
              <Label label="Éléments inclus" htmlFor="includedItems" />
              <Select
                isMulti
                id="includedItems"
                name="includedItems"
                options={getIncludedMenuItems()}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Sélectionner les éléments inclus"
                value={selectedIncludedItems}
                onChange={(selected) =>
                  setSelectedIncludedItems(selected as SelectOption[])
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                Si sélectionnés, seuls ces éléments seront proposés dans le
                composant
              </p>
            </div>

            <div>
              <Label label="Éléments exclus" htmlFor="excludedItems" />
              <Select
                isMulti
                id="excludedItems"
                name="excludedItems"
                options={getExcludedMenuItems()}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Sélectionner les éléments exclus"
                value={selectedExcludedItems}
                onChange={(selected) =>
                  setSelectedExcludedItems(selected as SelectOption[])
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                Si sélectionnés, ces éléments ne seront pas proposés dans le
                composant
              </p>
            </div>

            <div>
              <Label
                label="Types d'accompagnements autorisés"
                htmlFor="allowedSideTypes"
              />
              <Select
                isMulti
                id="allowedSideTypes"
                name="allowedSideTypes"
                options={sideTypeOptions}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Sélectionner les types d'accompagnements"
                value={selectedAllowedSideTypes}
                onChange={(selected) =>
                  setSelectedAllowedSideTypes(selected as SelectOption[])
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                Utilisé si aucune section ni éléments inclus ne sont spécifiés
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              isLoading={isLoading}
            >
              {isEditing ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MenuComboComponentModal;
